import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AuthColaboradorProvider, useAuthColaborador } from './contexts/AuthColaboradorContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Layout
import Layout from './components/layout/Layout';

// Páginas de Autenticação
import Login from './pages/auth/Login';
import LoginColaborador from './pages/LoginColaborador';
import ForgotPassword from './pages/auth/ForgotPassword';

// Páginas do Sistema
import Dashboard from './pages/Dashboard';
import Ponto from './pages/ponto/Ponto';
import PontoHistorico from './pages/ponto/PontoHistorico';
import FolhaPonto from './pages/ponto/FolhaPonto';
import Frota from './pages/frota/Frota';
import Veiculos from './pages/frota/Veiculos';
import HistoricoPontos from './pages/frota/HistoricoPontos';
import VeiculoManutencoes from './pages/frota/VeiculoManutencoes';
import Manutencoes from './pages/frota/Manutencoes';
import OrdensServico from './pages/frota/OrdensServico';
import Estoque from './pages/estoque/Estoque';
import RH from './pages/rh/RH';
import Colaboradores from './pages/rh/Colaboradores';
import Financeiro from './pages/financeiro/Financeiro';
import Relatorios from './pages/relatorios/Relatorios';
import RelatorioVeiculos from './pages/relatorios/RelatorioVeiculos';
import Configuracoes from './pages/Configuracoes';
import Atestados from './pages/rh/Atestados';
import AtestadosMotorista from './pages/Atestados';
import AbastecimentosFinanceiros from './pages/AbastecimentosFinanceiros';
import Beneficios from './pages/rh/Beneficios';
import Ferias from './pages/rh/Ferias';
import GestaoRH from './pages/rh/GestaoRH';



// Componentes
import Loading from './components/common/Loading';
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/common/ProtectedRoute';
import PWAInstallButton from './components/PWAInstallButton';

// Configuração do React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false, // Desabilitar retry globalmente
      refetchOnWindowFocus: false,
      staleTime: 0, // Sempre buscar dados frescos
      cacheTime: 5 * 60 * 1000, // 5 minutos
    },
    mutations: {
      retry: false,
    },
  },
});


// Componente principal da aplicação
function AppContent() {
  const { user, loading: userLoading } = useAuth();
  const { colaborador, loading: colaboradorLoading } = useAuthColaborador();

  // Aguardar carregamento de ambos os contextos
  if (userLoading || colaboradorLoading) {
    return <Loading />;
  }

  // Se não há usuário nem colaborador logado, mostrar telas de login
  if (!user && !colaborador) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/login-colaborador" element={<LoginColaborador />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={
          user?.perfil === 'administrador' ? 
            <Navigate to="/dashboard" replace /> : 
            <Navigate to="/ponto" replace />
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute 
            allowedProfiles={['administrador', 'supervisor', 'financeiro', 'administrativo', 'coordenador']}
            showAccessDenied={true}
          >
            <Dashboard />
          </ProtectedRoute>
        } />
        
        {/* Módulo de Ponto */}
        <Route path="/ponto" element={<Ponto />} />
        <Route path="/ponto/historico" element={<PontoHistorico />} />
        <Route path="/ponto/folha" element={<FolhaPonto />} />
        
        {/* Módulo de Atestados para Motoristas */}
        <Route path="/atestados" element={
          <ProtectedRoute allowedProfiles={['motorista']}>
            <AtestadosMotorista />
          </ProtectedRoute>
        } />
        
        {/* Módulo de Abastecimentos Financeiros para Supervisores, Administradores e Financeiro */}
        <Route path="/abastecimentos-financeiros" element={
          <ProtectedRoute allowedProfiles={['supervisor', 'administrador', 'financeiro']}>
            <AbastecimentosFinanceiros />
          </ProtectedRoute>
        } />
        
        {/* Módulo de Frota */}
        <Route path="/frota" element={
          <ProtectedRoute allowedProfiles={['administrador', 'supervisor', 'coordenador', 'administrativo', 'financeiro', 'motorista']}>
            <Frota />
          </ProtectedRoute>
        } />
        <Route path="/frota/veiculos" element={
          <ProtectedRoute allowedProfiles={['administrador', 'supervisor', 'coordenador', 'administrativo', 'financeiro']}>
            <Veiculos />
          </ProtectedRoute>
        } />
        <Route path="/frota/veiculos/:id/historico-pontos" element={
          <ProtectedRoute allowedProfiles={['administrador', 'supervisor', 'coordenador', 'administrativo', 'financeiro']}>
            <HistoricoPontos />
          </ProtectedRoute>
        } />
        <Route path="/frota/veiculos/:id/manutencoes" element={
          <ProtectedRoute allowedProfiles={['administrador', 'supervisor', 'coordenador', 'administrativo', 'financeiro']}>
            <VeiculoManutencoes />
          </ProtectedRoute>
        } />
        <Route path="/frota/manutencoes" element={
          <ProtectedRoute allowedProfiles={['administrador', 'supervisor', 'coordenador', 'administrativo', 'financeiro']}>
            <Manutencoes />
          </ProtectedRoute>
        } />
        <Route path="/frota/ordens-servico" element={
          <ProtectedRoute allowedProfiles={['administrador', 'supervisor', 'coordenador', 'administrativo', 'financeiro']}>
            <OrdensServico />
          </ProtectedRoute>
        } />

        {/* Estoque (Oficina e Outras áreas) */}
        <Route path="/estoque" element={
          <ProtectedRoute allowedProfiles={['administrador', 'supervisor', 'coordenador', 'administrativo', 'financeiro']}>
            <Estoque />
          </ProtectedRoute>
        } />
        
        {/* Módulo de RH */}
        <Route path="/rh" element={
          <ProtectedRoute allowedProfiles={['administrador', 'supervisor', 'coordenador', 'administrativo', 'financeiro']}>
            <RH />
          </ProtectedRoute>
        } />
        <Route path="/rh/colaboradores" element={
          <ProtectedRoute allowedProfiles={['administrador', 'supervisor', 'coordenador', 'administrativo']}>
            <Colaboradores />
          </ProtectedRoute>
        } />
        <Route path="/rh/atestados" element={
          <ProtectedRoute allowedProfiles={['administrador', 'supervisor', 'coordenador', 'administrativo']}>
            <Atestados />
          </ProtectedRoute>
        } />
        <Route path="/rh/beneficios" element={
          <ProtectedRoute allowedProfiles={['administrador', 'supervisor', 'coordenador', 'administrativo']}>
            <Beneficios />
          </ProtectedRoute>
        } />
        <Route path="/rh/ferias" element={
          <ProtectedRoute allowedProfiles={['administrador', 'supervisor', 'coordenador', 'administrativo']}>
            <Ferias />
          </ProtectedRoute>
        } />
        <Route path="/rh/gestao" element={
          <ProtectedRoute allowedProfiles={['administrador', 'supervisor', 'coordenador', 'administrativo']}>
            <GestaoRH />
          </ProtectedRoute>
        } />

        {/* Módulo Financeiro */}
        <Route path="/financeiro" element={
          <ProtectedRoute allowedProfiles={['administrador', 'supervisor', 'coordenador', 'financeiro']}>
            <Financeiro />
          </ProtectedRoute>
        } />
        
        {/* Relatórios */}
        <Route path="/relatorios" element={
          <ProtectedRoute allowedProfiles={['administrador', 'supervisor', 'coordenador', 'administrativo', 'financeiro']}>
            <Relatorios />
          </ProtectedRoute>
        } />
        <Route path="/relatorios/veiculos" element={
          <ProtectedRoute allowedProfiles={['administrador', 'supervisor', 'coordenador', 'administrativo', 'financeiro']}>
            <RelatorioVeiculos />
          </ProtectedRoute>
        } />
        
        {/* Configurações */}
        <Route path="/configuracoes" element={
          <ProtectedRoute allowedProfiles={['administrador', 'supervisor', 'coordenador']}>
            <Configuracoes />
          </ProtectedRoute>
        } />
        
        {/* Rota 404 */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <AuthProvider>
              <AuthColaboradorProvider>
                <div className="App">
                  <AppContent />
                  <PWAInstallButton />
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: '#363636',
                      color: '#fff',
                    },
                    success: {
                      duration: 3000,
                      iconTheme: {
                        primary: '#10B981',
                        secondary: '#fff',
                      },
                    },
                    error: {
                      duration: 5000,
                      iconTheme: {
                        primary: '#EF4444',
                        secondary: '#fff',
                      },
                    },
                  }}
                />
                </div>
              </AuthColaboradorProvider>
            </AuthProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;

